import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, FileText, Send, PenLine, Mail, MailOpen, MousePointerClick, DollarSign, MessageSquare, Plus, CheckCircle, XCircle, Clock, AlertCircle } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const EVENT_CONFIG = {
  created:               { icon: FileText,          color: "bg-zinc-600",     label: "Created" },
  sent:                  { icon: Send,               color: "bg-blue-600",     label: "Sent" },
  signed:                { icon: PenLine,            color: "bg-green-600",    label: "Signed" },
  followup_sent:         { icon: Mail,               color: "bg-blue-500",     label: "Follow-up" },
  email_opened:          { icon: MailOpen,           color: "bg-indigo-500",   label: "Opened" },
  email_clicked:         { icon: MousePointerClick,  color: "bg-indigo-400",   label: "Clicked" },
  followup_response:     { icon: MessageSquare,      color: "bg-amber-500",    label: "Response" },
  payment:               { icon: DollarSign,         color: "bg-emerald-600",  label: "Payment" },
  balance_sent:          { icon: Mail,               color: "bg-orange-500",   label: "Balance" },
  change_order_created:  { icon: Plus,               color: "bg-purple-600",   label: "Change Order" },
  change_order_sent:     { icon: Send,               color: "bg-purple-500",   label: "CO Sent" },
  change_order_approved: { icon: CheckCircle,        color: "bg-green-600",    label: "CO Approved" },
  change_order_rejected: { icon: XCircle,            color: "bg-red-600",      label: "CO Rejected" },
};

const STATUS_BADGES = {
  draft:        "bg-zinc-100 text-zinc-700",
  sent:         "bg-blue-100 text-blue-700",
  accepted:     "bg-green-100 text-green-700",
  signed:       "bg-green-100 text-green-700",
  paid:         "bg-emerald-100 text-emerald-700",
  partial_paid: "bg-amber-100 text-amber-700",
  voided:       "bg-red-100 text-red-700",
  inactive:     "bg-zinc-100 text-zinc-500",
};

function formatDate(ts) {
  if (!ts) return "";
  try {
    const d = new Date(ts);
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) +
      " at " + d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });
  } catch {
    return ts;
  }
}

function EventRow({ event, isLast }) {
  const cfg = EVENT_CONFIG[event.type] || { icon: Clock, color: "bg-zinc-400", label: event.type };
  const Icon = cfg.icon;
  return (
    <div className="flex gap-4" data-testid={`activity-event-${event.type}`}>
      {/* Timeline spine */}
      <div className="flex flex-col items-center">
        <div className={`flex items-center justify-center w-9 h-9 rounded-full ${cfg.color} shadow-sm shrink-0`}>
          <Icon className="w-4 h-4 text-white" />
        </div>
        {!isLast && <div className="w-px flex-1 bg-zinc-200 my-1" />}
      </div>

      {/* Content */}
      <div className={`pb-6 flex-1 min-w-0 ${isLast ? "" : ""}`}>
        <div className="flex items-start justify-between gap-2 flex-wrap">
          <p className="font-semibold text-zinc-900 text-sm leading-5">{event.title}</p>
          <time className="text-xs text-zinc-400 shrink-0 tabular-nums">{formatDate(event.timestamp)}</time>
        </div>
        {event.description && (
          <p className="text-sm text-zinc-500 mt-0.5 leading-relaxed">{event.description}</p>
        )}
        {event.meta?.user_agent && (
          <p className="text-xs text-zinc-400 mt-1 truncate">{event.meta.user_agent}</p>
        )}
      </div>
    </div>
  );
}

export default function QuoteActivityPage() {
  const { quoteId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/quotes/${quoteId}/activity`);
        setData(res.data);
      } catch (e) {
        setError(e.response?.data?.detail || "Failed to load activity log");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [quoteId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center">
        <div className="text-zinc-500 text-sm">Loading activity log...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center">
        <div className="text-center space-y-3">
          <AlertCircle className="w-10 h-10 text-red-400 mx-auto" />
          <p className="text-red-600 font-medium">{error}</p>
          <button onClick={() => navigate(-1)} className="text-sm text-zinc-500 underline">Go back</button>
        </div>
      </div>
    );
  }

  const statusClass = STATUS_BADGES[data.status] || "bg-zinc-100 text-zinc-600";

  return (
    <div className="min-h-screen bg-zinc-50">
      {/* Header */}
      <div className="bg-white border-b border-zinc-200 sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 py-4 flex items-center gap-4">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-1.5 text-zinc-500 hover:text-zinc-800 text-sm transition-colors"
            data-testid="activity-back-btn"
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-base font-bold text-zinc-900 tracking-tight">
                {data.quote_number}
              </h1>
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold uppercase tracking-wide ${statusClass}`}>
                {data.status}
              </span>
            </div>
            <p className="text-xs text-zinc-500 truncate mt-0.5">
              {data.customer_name}{data.property_address ? ` · ${data.property_address}` : ""}
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Link
              to={`/quotes/${quoteId}/edit`}
              className="text-xs font-medium text-[#00341d] hover:underline"
              data-testid="activity-edit-link"
            >
              Edit Quote
            </Link>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8">
        {/* Summary strip */}
        <div className="grid grid-cols-3 gap-3 mb-8">
          <div className="bg-white rounded-lg border border-zinc-200 p-4 text-center">
            <p className="text-xs uppercase tracking-wide text-zinc-400 mb-1">Events</p>
            <p className="text-2xl font-bold text-zinc-900">{data.events.length}</p>
          </div>
          <div className="bg-white rounded-lg border border-zinc-200 p-4 text-center">
            <p className="text-xs uppercase tracking-wide text-zinc-400 mb-1">Emails Opened</p>
            <p className="text-2xl font-bold text-zinc-900">
              {data.events.filter(e => e.type === "email_opened").length}
            </p>
          </div>
          <div className="bg-white rounded-lg border border-zinc-200 p-4 text-center">
            <p className="text-xs uppercase tracking-wide text-zinc-400 mb-1">Payments</p>
            <p className="text-2xl font-bold text-zinc-900">
              {data.events.filter(e => e.type === "payment").length}
            </p>
          </div>
        </div>

        {/* Timeline */}
        <div className="bg-white rounded-xl border border-zinc-200 p-6">
          <h2 className="text-xs font-semibold uppercase tracking-widest text-zinc-400 mb-6">Activity Timeline</h2>
          {data.events.length === 0 ? (
            <div className="text-center py-10 text-zinc-400">
              <Clock className="w-8 h-8 mx-auto mb-2 opacity-40" />
              <p className="text-sm">No activity recorded yet.</p>
            </div>
          ) : (
            <div>
              {data.events.map((event, i) => (
                <EventRow
                  key={`${event.type}-${i}`}
                  event={event}
                  isLast={i === data.events.length - 1}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
